import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";

const REVIEW_STATUSES = ["pending_review", "approved", "rejected", "needs_changes"];
const EXECUTION_STATUSES = ["not_executed", "ready_to_execute", "executed", "failed", "skipped"];

const EMPTY_COUNTS = {
  pending_review: 0,
  approved: 0,
  rejected: 0,
  needs_changes: 0,
  not_executed: 0,
  ready_to_execute: 0,
  executed: 0,
  failed: 0,
  skipped: 0,
};

const formatStatusLabel = (value) => String(value || "")
  .replace(/_/g, " ")
  .replace(/\b\w/g, (char) => char.toUpperCase());

const getReviewChipColor = (status) => {
  if (status === "approved") return "success";
  if (status === "rejected") return "error";
  if (status === "needs_changes") return "warning";
  return "default";
};

const getExecutionChipColor = (status) => {
  if (status === "executed") return "success";
  if (status === "failed") return "error";
  if (status === "skipped") return "warning";
  if (status === "ready_to_execute") return "info";
  return "default";
};

const safePreview = (artifact) => {
  const direct = artifact?.sql_preview || artifact?.preview;
  const content = direct || artifact?.sql_content || "";
  const text = String(content || "").trim();
  if (!text) return "SQL content is available from View SQL.";
  return text.length > 220 ? `${text.slice(0, 220)}…` : text;
};

const buildCounts = (artifacts) => {
  const counts = { ...EMPTY_COUNTS };
  (artifacts || []).forEach((artifact) => {
    const reviewStatus = artifact.review_status || "pending_review";
    const executionStatus = artifact.execution_status || "not_executed";
    counts[reviewStatus] = (counts[reviewStatus] || 0) + 1;
    counts[executionStatus] = (counts[executionStatus] || 0) + 1;
  });
  return counts;
};

const copyText = async (text) => {
  if (navigator?.clipboard?.writeText) {
    await navigator.clipboard.writeText(text || "");
    return;
  }
  const textarea = document.createElement("textarea");
  textarea.value = text || "";
  textarea.setAttribute("readonly", "readonly");
  textarea.style.position = "absolute";
  textarea.style.left = "-9999px";
  document.body.appendChild(textarea);
  textarea.select();
  document.execCommand("copy");
  document.body.removeChild(textarea);
};

const FullChangeSqlReviewPanel = ({ fullChangeId }) => {
  const [artifacts, setArtifacts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [savingId, setSavingId] = useState(null);
  const [error, setError] = useState(null);
  const [message, setMessage] = useState(null);
  const [viewArtifact, setViewArtifact] = useState(null);
  const [viewLoading, setViewLoading] = useState(false);
  const [actionDialog, setActionDialog] = useState(null);
  const [actionNotes, setActionNotes] = useState("");
  const [actionError, setActionError] = useState(null);

  const counts = useMemo(() => buildCounts(artifacts), [artifacts]);

  const loadArtifacts = async () => {
    if (!fullChangeId) {
      setArtifacts([]);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`/api/dev-ide/full-change/sql-artifacts?full_change_id=${encodeURIComponent(fullChangeId)}`);
      const data = await res.json();
      if (!data.success) throw new Error(data.error || (data.errors || []).join(", ") || "Failed to load SQL artifacts.");
      setArtifacts(data.sql_artifacts || []);
    } catch (e) {
      setError(e.message || "Failed to load SQL artifacts.");
      setArtifacts([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadArtifacts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fullChangeId]);

  const openSql = async (artifact) => {
    setViewLoading(true);
    setError(null);
    try {
      const res = await fetch(`/api/dev-ide/full-change/sql-artifacts/get?artifact_id=${encodeURIComponent(artifact.id)}`);
      const data = await res.json();
      if (!data.success) throw new Error(data.error || (data.errors || []).join(", ") || "Failed to load SQL artifact content.");
      setViewArtifact(data.sql_artifact || artifact);
    } catch (e) {
      setError(e.message || "Failed to load SQL artifact content.");
    } finally {
      setViewLoading(false);
    }
  };

  const openAction = (artifact, endpoint, fieldName, status, title, notesLabel, extra = {}) => {
    setActionError(null);
    setActionNotes("");
    setActionDialog({ artifact, endpoint, fieldName, status, title, notesLabel, extra });
  };

  const submitAction = async () => {
    if (!actionDialog?.artifact?.id) return;
    const notesValue = String(actionNotes || "").trim();
    if ((actionDialog.status === "rejected" || actionDialog.status === "skipped") && !notesValue) {
      setActionError(`${actionDialog.status === "rejected" ? "Rejection" : "Skip"} notes are required for SQL closeout audit evidence.`);
      return;
    }
    setSavingId(actionDialog.artifact.id);
    setActionError(null);
    setMessage(null);
    try {
      const body = {
        artifact_id: actionDialog.artifact.id,
        [actionDialog.fieldName]: actionDialog.status,
        notes: notesValue,
        ...actionDialog.extra,
      };
      if (actionDialog.status === "failed") {
        body.execution_error = actionNotes;
      }
      const res = await fetch(`/api/dev-ide/full-change/sql-artifacts/${actionDialog.endpoint}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      const data = await res.json();
      if (!data.success) throw new Error(data.error || (data.errors || []).join(", ") || "Failed to update SQL artifact.");
      setMessage(`SQL artifact ${actionDialog.artifact.sql_name || actionDialog.artifact.sql_file_path || actionDialog.artifact.id} updated.`);
      setActionDialog(null);
      setActionNotes("");
      await loadArtifacts();
    } catch (e) {
      setActionError(e.message || "Failed to update SQL artifact.");
    } finally {
      setSavingId(null);
    }
  };

  const handleCopyArtifact = async (artifact) => {
    try {
      let content = artifact?.sql_content;
      if (!content) {
        const res = await fetch(`/api/dev-ide/full-change/sql-artifacts/get?artifact_id=${encodeURIComponent(artifact.id)}`);
        const data = await res.json();
        if (!data.success) throw new Error(data.error || "Failed to load SQL content for copy.");
        content = data.sql_artifact?.sql_content || "";
      }
      await copyText(content);
      setMessage("SQL copied to clipboard for human review/manual execution.");
    } catch (e) {
      setError(e.message || "Failed to copy SQL content.");
    }
  };

  if (!fullChangeId || (!loading && !error && artifacts.length === 0)) {
    return null;
  }

  return (
    <Paper variant="outlined" sx={{ p: 2, mb: 2, borderColor: "warning.light", backgroundColor: "rgba(255,152,0,0.04)" }}>
      <Stack direction={{ xs: "column", md: "row" }} spacing={1} sx={{ justifyContent: "space-between", alignItems: { xs: "stretch", md: "flex-start" }, mb: 1 }}>
        <Box>
          <Typography variant="subtitle1">SQL Review</Typography>
          <Typography variant="caption" color="text.secondary">
            Human checkpoint for SQL artifacts linked to this Full Change. DEV IDE stores and tracks review/execution evidence only; it does not execute imported SQL automatically.
          </Typography>
        </Box>
        <Stack direction="row" spacing={0.75} useFlexGap sx={{ flexWrap: "wrap" }}>
          <Button size="small" variant="outlined" onClick={loadArtifacts} disabled={loading}>{loading ? "Refreshing..." : "Refresh SQL"}</Button>
        </Stack>
      </Stack>

      <Alert severity="warning" sx={{ mb: 1, py: 0.75 }}>
        Review SQL before running it manually or through a future explicit execution action. Record approved/rejected and execution outcomes here for audit evidence.
      </Alert>

      {error ? <Alert severity="error" sx={{ mb: 1 }}>{error}</Alert> : null}
      {message ? <Alert severity="success" sx={{ mb: 1 }}>{message}</Alert> : null}

      <Stack direction="row" spacing={0.75} useFlexGap sx={{ flexWrap: "wrap", mb: 1 }}>
        {REVIEW_STATUSES.map((status) => (
          <Chip key={status} size="small" label={`${formatStatusLabel(status)}: ${counts[status] || 0}`} color={getReviewChipColor(status)} variant={counts[status] ? "filled" : "outlined"} />
        ))}
        <Divider orientation="vertical" flexItem />
        {EXECUTION_STATUSES.map((status) => (
          <Chip key={status} size="small" label={`${formatStatusLabel(status)}: ${counts[status] || 0}`} color={getExecutionChipColor(status)} variant={counts[status] ? "filled" : "outlined"} />
        ))}
      </Stack>

      <Stack spacing={1}>
        {artifacts.map((artifact) => (
          <Paper key={artifact.id} variant="outlined" sx={{ p: 1.25, bgcolor: "background.default" }}>
            <Stack direction={{ xs: "column", lg: "row" }} spacing={1} sx={{ justifyContent: "space-between", alignItems: { xs: "stretch", lg: "flex-start" } }}>
              <Box sx={{ minWidth: 0 }}>
                <Typography variant="subtitle2" sx={{ overflowWrap: "anywhere" }}>
                  {artifact.sql_file_path || artifact.sql_name || `SQL Artifact #${artifact.id}`}
                </Typography>
                <Stack direction="row" spacing={0.5} useFlexGap sx={{ flexWrap: "wrap", mt: 0.5, mb: 0.75 }}>
                  <Chip size="small" label={`Type: ${formatStatusLabel(artifact.sql_type || "other")}`} variant="outlined" />
                  <Chip size="small" label={`Review: ${formatStatusLabel(artifact.review_status || "pending_review")}`} color={getReviewChipColor(artifact.review_status)} />
                  <Chip size="small" label={`Execution: ${formatStatusLabel(artifact.execution_status || "not_executed")}`} color={getExecutionChipColor(artifact.execution_status)} />
                  {artifact.dev_ide_change_id ? <Chip size="small" label={`DEV IDE Change #${artifact.dev_ide_change_id}`} variant="outlined" /> : null}
                </Stack>
                <Typography variant="caption" color="text.secondary" sx={{ display: "block", whiteSpace: "pre-wrap", fontFamily: "monospace", maxHeight: 72, overflow: "hidden" }}>
                  {safePreview(artifact)}
                </Typography>
                {artifact.review_notes ? <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.5 }}>Review notes: {artifact.review_notes}</Typography> : null}
                {artifact.execution_notes || artifact.execution_error ? (
                  <Typography variant="caption" color={artifact.execution_error ? "error" : "text.secondary"} sx={{ display: "block", mt: 0.5 }}>
                    Execution notes: {artifact.execution_error || artifact.execution_notes}
                  </Typography>
                ) : null}
              </Box>
              <Stack direction="row" spacing={0.5} useFlexGap sx={{ flexWrap: "wrap", justifyContent: { xs: "flex-start", lg: "flex-end" }, minWidth: { lg: 430 } }}>
                <Button size="small" variant="outlined" onClick={() => openSql(artifact)} disabled={viewLoading}>View SQL</Button>
                <Button size="small" variant="outlined" onClick={() => handleCopyArtifact(artifact)}>Copy SQL</Button>
                <Button size="small" variant="contained" color="success" onClick={() => openAction(artifact, "update-review-status", "review_status", "approved", "Approve SQL artifact", "Approval/review notes") } disabled={savingId === artifact.id}>Approve</Button>
                <Button size="small" variant="outlined" color="error" onClick={() => openAction(artifact, "update-review-status", "review_status", "rejected", "Reject SQL artifact", "Rejection notes") } disabled={savingId === artifact.id}>Reject</Button>
                <Button size="small" variant="outlined" color="success" onClick={() => openAction(artifact, "update-execution-status", "execution_status", "executed", "Mark SQL executed", "Execution evidence / notes") } disabled={savingId === artifact.id}>Executed</Button>
                <Button size="small" variant="outlined" color="error" onClick={() => openAction(artifact, "update-execution-status", "execution_status", "failed", "Mark SQL failed", "Failure details") } disabled={savingId === artifact.id}>Failed</Button>
                <Button size="small" variant="outlined" color="warning" onClick={() => openAction(artifact, "update-execution-status", "execution_status", "skipped", "Mark SQL skipped/not required", "Skip reason") } disabled={savingId === artifact.id}>Skipped</Button>
              </Stack>
            </Stack>
          </Paper>
        ))}
      </Stack>

      <Dialog open={Boolean(viewArtifact)} onClose={() => setViewArtifact(null)} fullWidth maxWidth="lg">
        <DialogTitle>SQL Artifact Review</DialogTitle>
        <DialogContent dividers>
          <Typography variant="subtitle2" sx={{ overflowWrap: "anywhere", mb: 1 }}>
            {viewArtifact?.sql_file_path || viewArtifact?.sql_name || "SQL artifact"}
          </Typography>
          <Alert severity="warning" sx={{ mb: 1 }}>
            Copying or viewing this SQL does not execute it. Run SQL only after human review through the approved database process.
          </Alert>
          <TextField
            fullWidth
            multiline
            minRows={14}
            value={viewArtifact?.sql_content || ""}
            InputProps={{ readOnly: true, sx: { fontFamily: "monospace", fontSize: 13 } }}
          />
          {viewArtifact?.rollback_sql_content ? (
            <>
              <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }}>Rollback SQL</Typography>
              <TextField
                fullWidth
                multiline
                minRows={8}
                value={viewArtifact.rollback_sql_content || ""}
                InputProps={{ readOnly: true, sx: { fontFamily: "monospace", fontSize: 13 } }}
              />
            </>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => handleCopyArtifact(viewArtifact)} disabled={!viewArtifact}>Copy SQL</Button>
          <Button onClick={() => setViewArtifact(null)}>Close</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(actionDialog)} onClose={() => setActionDialog(null)} fullWidth maxWidth="sm">
        <DialogTitle>{actionDialog?.title || "Update SQL artifact"}</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" sx={{ mb: 1, overflowWrap: "anywhere" }}>
            {actionDialog?.artifact?.sql_file_path || actionDialog?.artifact?.sql_name || "SQL artifact"}
          </Typography>
          {actionError ? <Alert severity="error" sx={{ mb: 1 }}>{actionError}</Alert> : null}
          <TextField
            fullWidth
            multiline
            minRows={4}
            label={actionDialog?.notesLabel || "Notes"}
            value={actionNotes}
            onChange={(event) => setActionNotes(event.target.value)}
            required={actionDialog?.status === "rejected" || actionDialog?.status === "skipped"}
            helperText={
              actionDialog?.status === "rejected" || actionDialog?.status === "skipped"
                ? "Required for closeout: rejected/skipped SQL artifacts must have audit notes."
                : "Notes are stored as review/execution evidence for this SQL artifact."
            }
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setActionDialog(null)} disabled={Boolean(savingId)}>Cancel</Button>
          <Button variant="contained" onClick={submitAction} disabled={Boolean(savingId)}>
            {savingId ? "Saving..." : "Save status"}
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

export default FullChangeSqlReviewPanel;
